// src/pages/MyListing.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container, Typography, Paper, Grid, CircularProgress, Box, Button, Snackbar,
    Tooltip, IconButton, TextField, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { styled } from '@mui/system';
import {
    getListing, updateListing, uploadListingImages,
    getBlockedPeriods, createBlockedPeriod,
    getTopLevelCategories, getSubcategories, deleteListing, deleteListingImages
} from '../services/api';

// Importing reusable components
import ImageGallery from '../components/ImageGallery';
import CategorySelector from '../components/CategorySelector';
import BlockedPeriodsCalendar from '../components/BlockedPeriodsCalendar';
import LocationChips from '../components/LocationChips';
import MapComponent from '../components/MapComponent';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
}));

function MyListing() {
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [blockedPeriods, setBlockedPeriods] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [images, setImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryPath, setCategoryPath] = useState([]);
    const { id } = useParams();
    const [locations, setLocations] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [zoomOpen, setZoomOpen] = useState(false);
    // State for image delete confirmation
    const [imageDeleteDialogOpen, setImageDeleteDialogOpen] = useState(false);
    const [imageToDeleteIndex, setImageToDeleteIndex] = useState(null);

    useEffect(() => {
        async function fetchListingAndBlockedPeriodsInline() {
            try {
                setLoading(true);
                const [listingResponse, blockedPeriodsResponse] = await Promise.all([
                    getListing(id),
                    getBlockedPeriods(id)
                ]);

                if (listingResponse?.data) {
                    setListing(listingResponse.data);
                    setTitle(listingResponse.data.title);
                    setDescription(listingResponse.data.description);
                    setPrice(listingResponse.data.price);
                    setImages(listingResponse.data.images);
                    setSelectedCategory(listingResponse.data.category);
                    setLocations(listingResponse.data.locations || []);
                }

                if (blockedPeriodsResponse?.data) {
                    // blockedPeriods should be an array of arrays: [[startDate1, endDate1], [startDate2, endDate2], ...]
                    setBlockedPeriods(blockedPeriodsResponse.data.map(period => [
                        new Date(period.start_date),
                        new Date(period.end_date)
                    ]));
                }
            } catch (error) {
                console.error('Error fetching listing and blocked periods', error);
                setError('An error occurred while loading the listing. Please try again later.');
            } finally {
                setLoading(false);
            }
        }
        fetchListingAndBlockedPeriodsInline();
    }, [id]);

    const handleLocationChange = (newLocation) => {
        if (locations.length < 3) {
            setLocations([...locations, { latitude: newLocation.lat, longitude: newLocation.lng }]);
        }
    };

    const handleLocationDelete = (index) => {
        setLocations(locations.filter((_, i) => i !== index));
    };

    const fetchTopLevelCategories = async () => {
        try {
            const response = await getTopLevelCategories();
            setCategories(response.data);
            setCategoryPath([]);
        } catch (error) {
            console.error('Error fetching top-level categories:', error);
            setSnackbar({ open: true, message: 'Failed to load categories. Please try again.' });
        }
    };

    const handleCategorySelect = async (category) => {
        try {
            const response = await getSubcategories(category.id);
            if (response.data.length > 0) {
                setCategories(response.data);
                setCategoryPath([...categoryPath, category]);
            } else {
                setSelectedCategory(category);
                setCategoryDialogOpen(false);
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSnackbar({ open: true, message: 'Failed to load subcategories. Please try again.' });
        }
    };

    const handleCategoryBack = async () => {
        if (categoryPath.length > 1) {
            const newPath = [...categoryPath];
            newPath.pop();
            setCategoryPath(newPath);
            const parentCategory = newPath[newPath.length - 1];
            const response = await getSubcategories(parentCategory.id);
            setCategories(response.data);
        } else {
            fetchTopLevelCategories();
        }
    };

    const handleSave = async () => {
        try {
            const updatedListing = {
                ...listing,
                title,
                description,
                price: parseFloat(price),
                category_id: selectedCategory.id,
                locations: locations,
            };
            await updateListing(id, updatedListing);
            setSnackbar({ open: true, message: 'Listing updated successfully!' });
        } catch (error) {
            console.error('Error updating listing', error);
            setSnackbar({ open: true, message: 'Failed to update listing. Please try again.' });
        }
    };

    const handleSaveBlockedPeriods = async () => {
        try {
            for (const [start, end] of blockedPeriods) {
                const startDate = start instanceof Date ? start : new Date(start);
                const endDate = end instanceof Date ? end : new Date(end);

                await createBlockedPeriod(id, {
                    start_date: startDate.toISOString(),
                    end_date: endDate.toISOString()
                });
            }
            setSnackbar({ open: true, message: 'Blocked periods saved successfully!' });
        } catch (error) {
            console.error('Error saving blocked periods', error);
            setSnackbar({ open: true, message: 'Failed to save blocked periods. Please try again.' });
        }
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('images', file));
            const response = await uploadListingImages(id, formData);
            // Prevent duplicate images from being added
            const uniqueNewImages = response.data.filter(newImg => !images.some(img => img.id === newImg.id));
            setImages([...images, ...uniqueNewImages]);
            setSnackbar({ open: true, message: 'Images uploaded successfully!' });
        } catch (error) {
            console.error('Error uploading images', error);
            setSnackbar({ open: true, message: 'Failed to upload images. Please try again.' });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImageClick = (index) => {
        setCurrentImageIndex(index);
    };

    // Open the delete confirmation dialog for an image
    const handleDeleteImageClick = (index, e) => {
        e.stopPropagation();
        setImageToDeleteIndex(index);
        setImageDeleteDialogOpen(true);
    };

    const handleConfirmDeleteImage = async () => {
        if (imageToDeleteIndex !== null) {
            const imageToDelete = images[imageToDeleteIndex];
            try {
                await deleteListingImages(id, [imageToDelete.image_url]);
                const newImages = images.filter((_, i) => i !== imageToDeleteIndex);
                setImages(newImages);
                if (currentImageIndex >= newImages.length) {
                    setCurrentImageIndex(0);
                }
                setSnackbar({ open: true, message: 'Image deleted successfully!' });
            } catch (error) {
                console.error('Error deleting image', error);
                setSnackbar({ open: true, message: 'Failed to delete image. Please try again.' });
            }
            setImageDeleteDialogOpen(false);
            setImageToDeleteIndex(null);
        }
    };

    const handleCancelDeleteImage = () => {
        setImageDeleteDialogOpen(false);
        setImageToDeleteIndex(null);
    };

    const handleDeleteListing = async () => {
        try {
            await deleteListing(id);
            setSnackbar({ open: true, message: 'Listing deleted successfully!' });
            setTimeout(() => window.location.href = '/my-listings', 2000);
        } catch (error) {
            console.error('Error deleting listing', error);
            setSnackbar({ open: true, message: 'Failed to delete listing. Please try again.' });
        }
    };

    if (loading) {
        return (
            <StyledContainer maxWidth="md">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </StyledContainer>
        );
    }

    if (error) {
        return (
            <StyledContainer maxWidth="md">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography color="error">{error}</Typography>
                </Box>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer maxWidth="md">
            <StyledPaper elevation={3}>
                <Typography variant="h4" gutterBottom>Edit Listing</Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                        {images && images.length > 0 ? (
                            <>
                                <Box sx={{ position: 'relative' }}>
                                    <ImageGallery
                                        images={images}
                                        currentImageIndex={currentImageIndex}
                                        onImageClick={handleImageClick}
                                    />
                                    <IconButton
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(255,255,255,0.8)',
                                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                                        }}
                                        onClick={() => setZoomOpen(true)}
                                    >
                                        <ZoomInIcon />
                                    </IconButton>
                                </Box>
                                {images.length > 1 && (
                                    <Grid container spacing={1} sx={{ mt: 2 }}>
                                        {images.map((image, index) => (
                                            <Grid item xs={3} key={image.id || index}>
                                                <Box sx={{ position: 'relative' }}>
                                                    <img
                                                        src={image.image_url}
                                                        alt={`${title} ${index + 1}`}
                                                        onClick={() => handleImageClick(index)}
                                                        style={{
                                                            width: '100%',
                                                            height: '80px',
                                                            objectFit: 'cover',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            opacity: index === currentImageIndex ? 0.6 : 1
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleDeleteImageClick(index, e)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: 0,
                                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                                            color: 'white',
                                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </>
                        ) : (
                            <Box
                                sx={{
                                    height: 400,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'grey.200',
                                    borderRadius: 2,
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6">No Image Available</Typography>
                            </Box>
                        )}
                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="upload-button-file"
                            multiple
                            type="file"
                            onChange={handleImageUpload}
                        />
                        <label htmlFor="upload-button-file">
                            <Button variant="contained" component="span" fullWidth sx={{ mt: 2 }}>
                                Upload Images
                            </Button>
                        </label>
                        <TextField
                            fullWidth
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Price per day"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Category"
                            value={selectedCategory ? selectedCategory.name : ''}
                            onClick={() => {
                                setCategoryDialogOpen(true);
                                fetchTopLevelCategories();
                            }}
                            InputProps={{ readOnly: true }}
                            margin="normal"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper elevation={2} sx={{ padding: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Blocked Periods
                                <Tooltip title="Select date ranges you want to block. Click 'Save Blocked Periods' when done.">
                                    <IconButton size="small">
                                        <InfoIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                            <BlockedPeriodsCalendar
                                blockedPeriods={blockedPeriods}
                                setBlockedPeriods={setBlockedPeriods}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleSaveBlockedPeriods}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Save Blocked Periods
                            </Button>
                            <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                                Select date ranges to block and click 'Save Blocked Periods'.
                            </Typography>
                        </Paper>
                        <Paper elevation={2} sx={{ padding: 2, mt: 4 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Item Locations (up to 3)
                            </Typography>
                            <MapComponent
                                center={locations[0] ? [locations[0].latitude, locations[0].longitude] : [40.7128, -74.0060]}
                                locations={locations}
                                onLocationChange={handleLocationChange}
                            />
                            <LocationChips
                                locations={locations}
                                handleDelete={handleLocationDelete}
                            />
                        </Paper>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mt: 4 }}>
                    <Grid item xs={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            fullWidth
                            disabled={!selectedCategory}
                        >
                            Save Changes
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setDeleteDialogOpen(true)}
                            fullWidth
                        >
                            Delete Listing
                        </Button>
                    </Grid>
                </Grid>

                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Delete Listing?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to permanently delete this listing? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleDeleteListing} color="error" autoFocus>
                            Confirm Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </StyledPaper>

            <CategorySelector
                open={categoryDialogOpen}
                handleClose={() => setCategoryDialogOpen(false)}
                categories={categories}
                handleCategorySelect={handleCategorySelect}
                handleCategoryBack={handleCategoryBack}
                hasBackButton={categoryPath.length > 0}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
            />

            {/* Zoom dialog for full-screen image view */}
            <Dialog open={zoomOpen} onClose={() => setZoomOpen(false)} maxWidth="lg">
                <Box sx={{ position: 'relative' }}>
                    <img
                        src={images[currentImageIndex]?.image_url}
                        alt={`${title} Zoomed`}
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '90vh',
                            objectFit: 'contain'
                        }}
                    />
                    <IconButton
                        onClick={() => setZoomOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Dialog>

            {/* Confirmation dialog for image deletion */}
            <Dialog
                open={imageDeleteDialogOpen}
                onClose={handleCancelDeleteImage}
            >
                <DialogTitle>Delete Image?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this image? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDeleteImage}>Cancel</Button>
                    <Button onClick={handleConfirmDeleteImage} color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </StyledContainer>
    );
}

export default MyListing;
